<template>
  <el-card shadow="never" class="lad-reviews--card">
    <template slot="header">
      <LadAvatar
        :src="_user.profile.avatar.src"
        :src-set="_user.profile.avatar.srcset"
      />

      <div class="card-header--right">
        <h3>{{ fullname }}</h3>
        <el-rate v-model="score" disabled aria-label="reviews" />

        <span>{{
          $dayjs(created_at, 'YYYY-MM-DD HH:mm:ss') | ladFnStrDate
        }}</span>
      </div>
    </template>

    <template v-if="message">
      <LadSummary
        :text="message"
        :max-length="200"
        :search="reviewsSearch"
        inline-toogle
      />

      <div class="card-actions">
        <LadVotes
          :likes="likes"
          :dislikes="dislikes"
          :disabled="voted"
          :message="$t(`${base}.opinion`)"
          @change="handleVote"
        />

        <div class="card-actions--comment">
          <el-button
            :id="`review-comments-btn-${review.uuid}`"
            type="text"
            size="small"
            :disabled="!comments.length"
            @click="toggleComments"
            >{{
              $tc(`${base}.comments.number`, comments.length, {
                comments: comments.length,
              })
            }}</el-button
          >

          <el-button type="text" size="small" @click="handleComment">{{
            $t(`${base}.comments.think_or_ask`)
          }}</el-button>
        </div>

        <transition name="fade-transform" mode="out-in">
          <LadReviewComments v-if="showComments" :comments="comments" />
        </transition>

        <transition name="fade-transform" mode="out-in">
          <LadReviewForm
            v-if="showForm"
            :review="review"
            @success="handleSuccess"
          />
        </transition>

        <transition v-if="showActions" name="fade-transform" mode="out-in">
          <div class="card-actions--book">
            <el-button type="text" size="small" @click="handleComment">{{
              $t(`${base}.keep_asking`)
            }}</el-button>

            <el-button
              type="success"
              size="small"
              @click="handleShowAvailability"
              >{{ $t(`${base}.layaway`) }}</el-button
            >
          </div>
        </transition>
      </div>
    </template>
  </el-card>
</template>

<script>
import { mapState } from 'vuex'
import LadAvatar from '@/components/avatar'
import LadSummary from '@/components/summary'
import LadVotes from '@/components/votes'
import LadReviewComments from './comments'
import LadReviewForm from './form'

export default {
  name: 'LadReviewCard',
  components: {
    LadAvatar,
    LadSummary,
    LadVotes,
    LadReviewForm,
    LadReviewComments,
  },
  props: {
    review: { type: Object, required: true },
    uuid: { type: String, required: true },
    message: { type: String, required: true },
    score: { type: Number, required: true },
    // eslint-disable-next-line vue/prop-name-casing
    likes_dislikes: { type: String, required: true },
    // likes: { type: Number, required: true },
    // dislikes: { type: Number, required: true },
    // eslint-disable-next-line vue/prop-name-casing
    created_at: { type: String, required: true },
    // eslint-disable-next-line vue/prop-name-casing
    _user: { type: Object, required: true },
    comments: { type: Array, default: () => [] },
  },
  data() {
    return {
      base: 'components.reviews',
      showComments: false,
      showActions: false,
      showForm: false,
      voted: false,
    }
  },
  computed: {
    ...mapState('reviews', {
      reviews: 'reviews',
      reviewsSearch: 'search',
    }),
    likes() {
      return parseInt(this.likes_dislikes.split('/')[0])
    },
    dislikes() {
      return parseInt(this.likes_dislikes.split('/')[1])
    },
    fullname() {
      const firstname = this._user.profile.firstname
        ? this._user.profile.firstname
        : ''
      const lastname = this._user.profile.lastname
        ? this._user.profile.lastname
        : ''

      return `${firstname} ${lastname}`.trim()
    },
  },
  watch: {
    isAuth(val) {
      if (val && this.showActions) {
        this.showActions = false
        this.showForm = true
      }
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      const firstReview = this.$route.query.first_review

      if (firstReview && this.review.uuid === firstReview) {
        this.showActions = true
      }
    },
    async handleVote(vote) {
      try {
        const token = await this.$recaptcha.execute('reviews_vote')

        const { data, status } = await this.$axios.post(
          `/api/reviews/${this.review.uuid}/vote`,
          {
            vote,
            message: { uuid: this.uuid },
            token,
          }
        )

        if (status === 'success') {
          this.voted = true

          this.LadNextTick(() => this.handleUpdate(data), 'LadReviewCard')
        }
      } catch (e) {}
    },
    handleComment() {
      if (this.isAuth) {
        this.showActions = false
        this.showForm = true
      } else {
        this.$store.dispatch('modals/toggle', 'auth')
      }
    },
    handleShowAvailability() {
      this.$store.dispatch('modals/toggle', 'availability')
    },
    toggleComments() {
      this.showComments = !this.showComments
    },
    handleSuccess(data) {
      if (!this.showComments) {
        this.toggleComments()
      }

      this.handleUpdate(data)
    },
    handleUpdate(data) {
      const reviews = this.reviews.map((review) => {
        return review.uuid === data.uuid ? data : review
      })

      this.$store.dispatch('reviews/setProductReviews', reviews)
    },
  },
}
</script>

<style lang="scss">
.lad-reviews {
  &--card {
    position: relative;

    & > .el-card__header {
      padding: 16px;
      display: grid;
      grid-column-gap: 20px;
      align-items: center;
      grid-template-columns: auto 1fr;
    }

    & > .el-card__body {
      padding: 16px;

      &:empty {
        display: none;
      }
    }

    .card-header--right {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: 1fr auto;

      h3 {
        margin: 0;
      }

      span {
        font-style: italic;
        font-size: 12px;
      }
    }

    .card-actions {
      display: grid;
      align-items: baseline;
      grid-template-columns: 1fr auto;

      &--comment {
        .el-button--text:first-child {
          color: #999;
        }
      }

      &--book {
        text-align: right;
        grid-column: 1 / 3;
        margin-top: 10px;
      }
    }

    .lad-reviews--comments {
      grid-column-start: 1;
      grid-column-end: 3;
    }
  }

  &--form {
    border-top: 1px solid #eee;
    margin-top: 10px;
    padding-top: 10px;
    grid-column-start: 1;
    grid-column-end: 3;
  }

  &--comments + &--form {
    border-top: none;
    margin-top: 0;
  }

  &--sm {
    .lad-reviews--card {
      .el-card__header {
        grid-template-columns: 1fr auto;
        align-items: center;

        & > .lad-avatar {
          order: 1;
        }
      }

      .card-header--right {
        grid-template-columns: 1fr;

        .el-rate {
          order: 1;
        }
      }

      .card-actions {
        .lad-votes {
          & > span {
            display: none;
          }
        }

        .el-button + .el-button {
          margin-left: 5px;
        }
      }
    }
  }
}
</style>
